import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  Switch,
  TextField,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import AddressAutocomplete from '../../../shared/components/Forms/AddressAutocomplete.component';
import { ParsedAddress, buildAddressLabel } from '../../../shared/components/Forms/parse-google-address.util';
import { ZIP_CODE_FORMAT_ERROR, ZIP_CODE_PATTERN } from '../../../shared/components/Forms/address-form.constants';
import { REGION_PLACE_TYPES } from '../../constants/delivery-city-place.constants';
import { useLiveDeliveryZoneOptions } from '../../hooks/use-live-delivery-zone-options.hook';
import { CreateDeliveryCityRequestDTO } from '../../types/dtos/create-delivery-city.dto';
import { DELIVERY_CITY_PLACE_HINT, isDeliveryCityNameValid } from '../../utils/delivery-city-form-rules.util';

const ID_PREFIX = 'create-delivery-city';

type Props = {
  open: boolean;
  isSubmitting: boolean;
  onSubmit: (data: CreateDeliveryCityRequestDTO) => Promise<void> | void;
  onClose: () => void;
};

const useStyles = makeStyles((theme) => ({
  fields: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    paddingTop: '0.5rem',

    '& .MuiOutlinedInput-root.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.admin.primary.main,
    },
    '& .MuiInputLabel-outlined.Mui-focused:not(.Mui-error)': {
      color: theme.palette.admin.primary.main,
    },
  },
  row: {
    display: 'flex',
    gap: '1rem',

    '& > *': {
      flex: 1,
    },
  },
  switchBase: {
    '&$switchChecked': {
      color: theme.palette.admin.primary.main,
    },
    '&$switchChecked + $switchTrack': {
      backgroundColor: theme.palette.admin.primary.main,
    },
  },
  switchChecked: {},
  switchTrack: {},
  createButton: {
    backgroundColor: theme.palette.admin.primary.main,
    color: theme.palette.admin.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.admin.primary.dark,
    },
  },
}));

export function CreateDeliveryCityDialog({ open, isSubmitting, onSubmit, onClose }: Props) {
  const classes = useStyles();
  const { zoneOptions } = useLiveDeliveryZoneOptions();

  const [placeLabel, setPlaceLabel] = useState('');
  const [isPlaceSelected, setIsPlaceSelected] = useState(false);
  const [name, setName] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [zoneId, setZoneId] = useState('');
  const [isActive, setIsActive] = useState(true);
  const lastSelectedPlaceLabelRef = useRef<string | null>(null);

  useEffect(() => {
    if (!open) return;

    lastSelectedPlaceLabelRef.current = null;
    setPlaceLabel('');
    setIsPlaceSelected(false);
    setName('');
    setZipCode('');
    setZoneId('');
    setIsActive(true);
  }, [open]);

  const handlePlaceSelected = useCallback((address: ParsedAddress) => {
    lastSelectedPlaceLabelRef.current = buildAddressLabel(address);
    setName(address.city);
    setZipCode(address.zipCode);
    setIsPlaceSelected(true);
  }, []);

  const handlePlaceLabelChange = useCallback((label: string) => {
    setPlaceLabel(label);

    if (label === lastSelectedPlaceLabelRef.current) return;

    lastSelectedPlaceLabelRef.current = null;
    setIsPlaceSelected(false);
    setName('');
    setZipCode('');
  }, []);

  const selectedZoneId = zoneId !== '' ? zoneId : zoneOptions[0]?.id ?? '';
  const isZipCodeInvalid = zipCode !== '' && !ZIP_CODE_PATTERN.test(zipCode);
  const canSubmit =
    isPlaceSelected &&
    isDeliveryCityNameValid(name) &&
    ZIP_CODE_PATTERN.test(zipCode) &&
    selectedZoneId !== '' &&
    !isSubmitting;

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const handleSubmit = () => {
    if (!canSubmit) return;

    onSubmit({ name: name.trim(), zipCode, zoneId: selectedZoneId, isActive });
  };

  return (
    <Dialog open={open} onClose={handleClose} disableEscapeKeyDown={isSubmitting} fullWidth maxWidth="sm">
      <DialogTitle>Créer une ville de livraison</DialogTitle>
      <DialogContent>
        <div className={classes.fields}>
          <AddressAutocomplete
            id={`${ID_PREFIX}-place`}
            label="Ville"
            size="small"
            types={REGION_PLACE_TYPES}
            value={placeLabel}
            onChange={handlePlaceLabelChange}
            onAddressSelected={handlePlaceSelected}
            helperText={isPlaceSelected ? undefined : DELIVERY_CITY_PLACE_HINT}
          />
          <div className={classes.row}>
            <TextField
              size="small"
              variant="outlined"
              required
              id={`${ID_PREFIX}-zip-code`}
              label="Code postal"
              disabled={!isPlaceSelected}
              inputProps={{ inputMode: 'numeric', maxLength: 5 }}
              error={isZipCodeInvalid}
              helperText={isZipCodeInvalid ? ZIP_CODE_FORMAT_ERROR : undefined}
              value={zipCode}
              onChange={(event) => setZipCode(event.target.value)}
            />
            <FormControl variant="outlined" size="small" required>
              <InputLabel htmlFor={`${ID_PREFIX}-zone`}>Zone</InputLabel>
              <Select
                native
                label="Zone"
                value={selectedZoneId}
                inputProps={{ id: `${ID_PREFIX}-zone` }}
                onChange={(event: React.ChangeEvent<HTMLSelectElement>) => setZoneId(event.target.value)}
              >
                {zoneOptions.map((zone) => (
                  <option key={zone.id} value={zone.id}>
                    {zone.name}
                  </option>
                ))}
              </Select>
            </FormControl>
          </div>
          <FormControlLabel
            control={
              <Switch
                size="small"
                color="default"
                classes={{ switchBase: classes.switchBase, checked: classes.switchChecked, track: classes.switchTrack }}
                checked={isActive}
                onChange={(event) => setIsActive(event.target.checked)}
              />
            }
            label="Actif"
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button size="small" variant="outlined" onClick={handleClose} disabled={isSubmitting}>
          Annuler
        </Button>
        <Button
          size="small"
          variant="contained"
          className={classes.createButton}
          onClick={handleSubmit}
          disabled={!canSubmit}
          startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
        >
          Créer
        </Button>
      </DialogActions>
    </Dialog>
  );
}
